import dayjs from "dayjs"
import React from "react"
import { twMerge } from "tailwind-merge"

import { Button } from "../../components/shared/Buttons"
import Typography from "../../components/shared/Typography"

import { useBookableEvents } from "./BookableEventsContext"

const BookableEventCard = ({ event, onClick }) => {
  const { theme } = useBookableEvents()

  // Helper to get border radius classes based on theme
  const getImageBorderRadius = () => {
    if (!theme) return "rounded-t-xl"
    switch (theme.buttonStyle) {
      case "square":
        return "rounded-t-none"
      case "rounded":
        return "rounded-t-xl"
      case "pill":
        return "rounded-t-3xl"
      default:
        return "rounded-t-xl"
    }
  }

  const getCardBorderRadius = () => {
    if (!theme) return "rounded-xl"
    switch (theme.buttonStyle) {
      case "square":
        return "rounded-none"
      case "rounded":
        return "rounded-xl"
      case "pill":
        return "rounded-3xl"
      default:
        return "rounded-xl"
    }
  }

  const getDateBadgeBorderRadius = () => {
    if (!theme) return "rounded-tl-xl"
    switch (theme.buttonStyle) {
      case "square":
        return "rounded-tl-none"
      case "rounded":
        return "rounded-tl-xl"
      case "pill":
        return "rounded-tl-3xl"
      default:
        return "rounded-tl-xl"
    }
  }

  // Helper to get button border radius based on theme
  const getButtonBorderRadius = () => {
    if (!theme) return undefined
    switch (theme.buttonStyle) {
      case "square":
        return "0px"
      case "rounded":
        return "12px"
      case "pill":
        return "50px"
      default:
        return "12px"
    }
  }

  // Helper to get button classes based on theme
  const getButtonClasses = () => {
    if (!theme) return ""
    switch (theme.buttonStyle) {
      case "square":
        return "!rounded-none"
      case "rounded":
        return "!rounded-xl"
      case "pill":
        return "!rounded-full"
      default:
        return "!rounded-xl"
    }
  }

  // Generate button styles for both v1 and v2 themes
  const getButtonStyle = () => {
    if (!theme) return undefined
    return {
      backgroundColor: `${theme.colors?.primary} !important`,
      borderColor: `${theme.colors?.primary} !important`,
      color: `${theme.colors?.textLight} !important`,
      fontFamily: theme.fonts?.paragraph,
      borderRadius: theme.borderRadius || getButtonBorderRadius()
    }
  }

  const handleButtonHover = (e, isHovering) => {
    if (!theme) return
    e.target.style.backgroundColor = isHovering ? theme.colors?.primaryDark : theme.colors?.primary
    e.target.style.borderColor = isHovering ? theme.colors?.primaryDark : theme.colors?.primary
  }

  return (
    <a
      className={twMerge("group relative max-w-64 border border-gray-light text-center", getCardBorderRadius())}
      href="#"
      onClick={(e) => {
        e.preventDefault()
        onClick()
      }}>
      {event.photoUrl ? (
        <img
          src={event.photoUrl}
          alt={event.name}
          className={twMerge("box-content max-h-48 w-full object-cover", getImageBorderRadius())}
        />
      ) : (
        <div className={twMerge("aspect-square w-full bg-gray-light", getImageBorderRadius())} />
      )}
      <div className={twMerge("absolute left-0 top-0 bg-black p-2 text-center text-white", getDateBadgeBorderRadius())}>
        <div className="text-sm uppercase">{dayjs(event.startsAt).format("MMM")}</div>
        <div className="text-4xl">{dayjs(event.startsAt).format("D")}</div>
      </div>
      <div className="flex flex-col gap-4 p-8">
        <Typography variant="h3" className="group-hover:underline">
          {event.name}
        </Typography>
        <div>
          {dayjs(event.startsAt).format("h:mm A")} - {dayjs(event.endsAt).format("h:mm A z")}
        </div>
        <div className="truncate">{event.description}</div>
        <Button
          type={theme ? "skip-default-classnames" : "primary"}
          className={twMerge(
            theme
              ? "h-8 border px-4 font-bold text-white transition-all hover:shadow focus:outline-none focus:ring-2 focus:ring-blue"
              : "",
            getButtonClasses()
          )}
          style={getButtonStyle()}
          onMouseEnter={(e) => handleButtonHover(e, true)}
          onMouseLeave={(e) => handleButtonHover(e, false)}>
          Edit event
        </Button>
      </div>
    </a>
  )
}

export default BookableEventCard
